import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useCan } from "../../auth/Can";
import { useMaybeAuth } from "../../auth/AuthContext";
import { ApprovalRow, decideApproval, listApprovals, requestApproval } from "../../services/supportService";

/**
 * Phase 2 approvals (§5.8) on a ticket: ask for compensation / a fee waiver / a suspension lift, and approve or reject
 * pending ones. The server enforces who may decide (cap, admin-only, no self-approval) — refusals are shown here.
 */
export const APPROVAL_KIND_LABELS: Record<string, string> = { compensation: "Compensation", waive_fee: "Waive fee", lift_suspension: "Lift suspension" };
const STATUS_COLOR: Record<string, "warning" | "success" | "default"> = { pending: "warning", approved: "success", rejected: "default" };

const ApprovalsPanel: React.FC<{ ticketId: string | number }> = ({ ticketId }) => {
    const { can, canAny } = useCan();
    const me = useMaybeAuth()?.me ?? null;
    const canWrite = can("support:write");
    // R2 D3: only show decisions the server would accept (approver tick, not your own request, lift = admin only).
    const isSuper = me?.role === "SUPER_ADMIN";
    const approver = isSuper || canAny(["wallet:adjust", "support:lead"] as any);
    const mayDecide = (a: ApprovalRow) => a.status === "pending" && approver && a.requestedBy !== me?.id && (a.kind !== "lift_suspension" || isSuper);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<ApprovalRow[]>([]);
    const [kind, setKind] = useState("compensation");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [error, setError] = useState<string | null>(null);

    const [cap, setCap] = useState<{ cap: number; approvedTotal: number } | null>(null);
    const load = useCallback(() => {
        listApprovals({ ticketId }).then((r) => { setRows(r.data.approvals); setCap(r.data.cap != null ? { cap: r.data.cap, approvedTotal: r.data.approvedTotal ?? 0 } : null); }).catch(() => setRows([]));
    }, [ticketId]);
    useEffect(() => { load(); }, [load]);

    const fail = (e: any, fallback: string) => setError(e?.response?.data?.message || fallback);
    const submit = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await requestApproval(ticketId, { kind, note: note.trim(), ...(kind === "compensation" ? { amount: Number(amount) } : {}) });
            setNote(""); setAmount(""); setError(null);
            load();
        } catch (e) { fail(e, "Could not send the request"); } finally { setSaving(false); }
    };
    const decide = async (id: string, decision: "approve" | "reject") => {
        if (saving) return;
        setSaving(true);
        try {
            await decideApproval(id, decision);
            setError(null);
            load();
        } catch (e) { fail(e, "Could not save the decision"); } finally { setSaving(false); }
    };
    const ready = !!note.trim() && (kind !== "compensation" || Number(amount) >= 1);

    return (
        <Box sx={{ mt: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2 }} data-testid="approvals-panel">
            <Typography variant="caption" sx={{ fontWeight: 700 }}>APPROVALS</Typography>
            {cap && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} data-testid="approvals-cap">Cap ₹{cap.cap} per ticket · ₹{cap.approvedTotal} approved so far</Typography>}
            {rows.map((a) => (
                <Stack key={a.id} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                    <Chip size="small" color={STATUS_COLOR[a.status] ?? "default"} label={a.status} />
                    <Typography variant="body2">{APPROVAL_KIND_LABELS[a.kind] ?? a.kind}{a.amount != null ? ` ₹${a.amount}` : ""} — {a.note}</Typography>
                    {/* Round 2 F11: approving moves no money — show whether it was paid by hand yet. */}
                    {a.status === "approved" && a.kind !== "lift_suspension" && (a.paidAt
                        ? <Chip size="small" color="success" variant="outlined" data-testid={`paid-${a.id}`} label={`Paid ${new Date(a.paidAt).toLocaleDateString()}${a.paidNote ? ` — ${a.paidNote}` : ""}`} />
                        : <Chip size="small" color="warning" variant="outlined" data-testid={`to-pay-${a.id}`} label={a.kind === "waive_fee" ? "Approved — waiver to do" : "Approved — to pay"} />)}
                    {mayDecide(a) && (
                        <>
                            <Button size="small" disabled={saving} onClick={() => decide(a.id, "approve")}>Approve</Button>
                            <Button size="small" color="inherit" disabled={saving} onClick={() => decide(a.id, "reject")}>Reject</Button>
                        </>
                    )}
                </Stack>
            ))}
            {canWrite && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                    <Select size="small" value={kind} onChange={(e) => setKind(String(e.target.value))} inputProps={{ "aria-label": "Approval kind" }}>
                        {Object.entries(APPROVAL_KIND_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                    </Select>
                    {kind === "compensation" && <TextField size="small" label="Amount ₹" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ width: 120 }} />}
                    <TextField size="small" label="Why" value={note} onChange={(e) => setNote(e.target.value)} inputProps={{ maxLength: 500 }} sx={{ minWidth: 200 }} />
                    <Button size="small" variant="outlined" disabled={!ready || saving} onClick={submit}>Ask for approval</Button>
                </Stack>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </Box>
    );
};

export default ApprovalsPanel;
